import { useMutation, useQueryClient } from "@tanstack/react-query";

import { invalidateToolRegistryQueries } from "./cache-utils";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

interface UpdateToolRegistryParams {
  workspaceId: number;
  toolId: number;
  name?: string;
  description?: string | null;
  folderId?: number | null;
}

interface UpdateToolRegistryResponse {
  success: boolean;
  tool_registry: {
    id: number;
    name: string;
    description: string | null;
    workspace_id: number;
  };
}

export const useUpdateToolRegistry = () => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      toolId,
      name,
      description,
      folderId,
    }: UpdateToolRegistryParams): Promise<UpdateToolRegistryResponse> => {
      const body: Record<string, unknown> = {};
      if (name !== undefined) {
        body.name = name;
      }
      if (description !== undefined) {
        body.description = description;
      }
      if (folderId !== undefined) {
        body.folder_id = folderId;
      }

      const res = await fetch(ENDPOINTS.tool_registry.get(workspaceId, toolId), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      });
      return handleResponse(res);
    },
    onSuccess: (_data, variables) => {
      invalidateToolRegistryQueries(queryClient, variables.workspaceId);
    },
    onError: error => {
      displayErrorToast(error);
    },
  });
};
